import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useEffect, useRef, useState } from 'react';
import { packageData } from "@/mocks/package";
import { services } from "@/mocks/home";
import { usePageSEO } from '@/hooks/usePageSEO';
import CaseStudiesSection from './components/CaseStudiesSection';

const mainframeSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Mainframe Solutions — Executive Search & Workforce Infrastructure',
    url: 'https://jeffdume.com/mainframe-solutions',
    description: 'Mainframe Solutions is an operational advisory and delivery partner that closes the loop between how organizations hire, how they operate, and how they govern technology. Executive search, AI governance, software development, and digital infrastructure.',
    provider: {
      '@type': 'Person',
      name: 'Jeff Dumè',
      url: 'https://jeffdume.com',
    },
    areaServed: 'United States',
    serviceType: ['Executive Search', 'AI Governance', 'Software Development', 'Operational Systems', 'Workforce Infrastructure', 'Digital Infrastructure'],
    offers: {
      '@type': 'Offer',
      price: '50000',
      priceCurrency: 'USD',
      description: 'Infrastructure Growth Package — connected hiring, governance, software, and digital infrastructure.',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Mainframe Solutions',
    url: 'https://mainframesolutions.net/',
    description: 'Operational advisory and delivery partner specializing in executive search, AI governance, structured hiring, and workforce infrastructure.',
    founder: { '@type': 'Person', name: 'Jeff Dumè' },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Houston',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
    sameAs: ['https://www.linkedin.com/in/jdume/', 'https://mainframesolutions.net/'],
  },
];

function useScrollReveal(threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

export default function MainframeSolutions() {
  usePageSEO({
    title: 'Mainframe Solutions | Executive Search, AI Governance & Workforce Infrastructure | Houston, TX',
    description: 'Mainframe Solutions delivers executive search, AI governance, software development, and operational infrastructure as one accountable partner. Reduce hiring risk and modernize your operational backbone. Houston, TX.',
    keywords: 'Mainframe Solutions, executive search Houston, AI governance consulting, workforce infrastructure, structured hiring systems, operational systems, construction recruiting, software development Texas, BuildFit AI',
    canonical: '/mainframe-solutions',
    ogTitle: 'Mainframe Solutions | Executive Search, AI Governance & Workforce Infrastructure',
    ogDescription: 'One accountable partner for executive search, AI governance, software development, and operational infrastructure. Reduce hiring risk and scale high-performing teams.',
    schema: mainframeSchema,
    breadcrumbs: [{ name: 'Mainframe Solutions', item: '/mainframe-solutions' }],
  });

  const heroReveal = useScrollReveal();
  const taglineReveal = useScrollReveal();
  const decisionReveal = useScrollReveal();
  const frameworkReveal = useScrollReveal();
  const servicesHeaderReveal = useScrollReveal();
  const servicesListReveal = useScrollReveal(0.1);
  const loopReveal = useScrollReveal();
  const whyReveal = useScrollReveal();
  const packageReveal = useScrollReveal();
  const ctaReveal = useScrollReveal();
  const ecosystemReveal = useScrollReveal();

  const decisionSteps = [
    {
      title: 'Assess Decisions',
      desc: 'We identify where hiring risk, decision ownership, AI governance exposure, and operational alignment intersect — and where gaps in one area create compounding problems across the organization.',
    },
    {
      title: 'Evaluate Consistently',
      desc: 'Structured evaluation frameworks are used in every engagement. Hiring decisions, system deployments, and governance policies are measured against consistent, documented criteria — not intuition or precedent.',
    },
    {
      title: 'Operate in Reality',
      desc: 'Every recommendation is designed for actual operational use, not theoretical best practice. Systems are deployed into daily workflows where they are measured by outcomes, not by intent.',
    },
  ];

  const loopSteps = [
    {
      num: '1',
      label: 'Assess Decisions',
      sub: 'Hiring Risk & Readiness Assessment',
      desc: 'The Hiring Risk and Readiness Assessment identifies governance gaps, software deficiencies, and talent requirements across the organization.',
    },
    {
      num: '2',
      label: 'Govern',
      sub: 'MainGov',
      desc: 'Oversight policies and accountability frameworks are established for AI use across hiring decisions and operational systems.',
    },
    {
      num: '3',
      label: 'Build',
      sub: 'MainSoft',
      desc: 'Operational software, AI tools, and workflow infrastructure are designed and deployed to address the requirements identified in the assessment.',
    },
    {
      num: '4',
      label: 'Evaluate',
      sub: 'BuildFit AI',
      desc: 'Structured candidate evaluation is applied to every leadership search using BuildFit AI scoring and documented criteria.',
    },
    {
      num: '5',
      label: 'Place',
      sub: 'MainRecruit',
      desc: 'Leadership professionals, AI engineers, and technology personnel are recruited and placed to operate the systems the organization has built.',
    },
    {
      num: '6',
      label: 'Sustain',
      sub: 'MainWeb',
      desc: 'Digital infrastructure, HubSpot automation, and operational systems are maintained and refined as organizational requirements evolve.',
    },
  ];

  const whyItems = [
    {
      title: 'Industry Recognition',
      desc: 'Our approach to structured hiring evaluation and AI governance is presented at the national level to HR executives and compliance leaders.',
    },
    {
      title: 'Single Accountable Partner',
      desc: 'Governance, software, talent, and digital infrastructure managed under one engagement model with no coordination gaps between vendors.',
    },
    {
      title: 'Proprietary Technology',
      desc: 'BuildFit AI is not a third-party tool we license or recommend to clients. Mainframe Solutions built, owns, and operates directly.',
    },
  ];

  const ecosystemPartners = [
    { name: 'BuildFit AI', icon: 'ri-brain-line' },
    { name: 'Mainframe Solutions', icon: 'ri-server-line' },
    { name: 'Microsoft Partner Ecosystem', icon: 'ri-windows-line' },
    { name: 'HubSpot Solutions Provider', icon: 'ri-bar-chart-box-line' },
    { name: 'SHRM', icon: 'ri-shield-user-line' },
    { name: 'HR Houston', icon: 'ri-group-line' },
    { name: 'AGC of Texas', icon: 'ri-building-2-line' },
    { name: 'Houston Contractors Association', icon: 'ri-community-line' },
    { name: 'Regional Hispanic Contractors Association', icon: 'ri-team-line' },
    { name: 'University of Oxford', icon: 'ri-graduation-cap-line' },
    { name: 'Lund University', icon: 'ri-book-open-line' },
  ];

  return (
    <div className="bg-charcoal min-h-screen">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative pt-20 sm:pt-24 md:pt-32 pb-12 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-[#1a1a1a] to-[#111]"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-charcoal/60"></div>
          <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div ref={heroReveal.ref}>
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">Operational Systems &amp; Executive Search Partner</p>
              <h1 className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-display font-bold leading-tight mb-4 sm:mb-6">Mainframe Solutions</h1>
              <div className={`transition-all duration-700 delay-100 ${heroReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                <p className="text-white/60 text-sm md:text-base max-w-2xl leading-relaxed mb-6 sm:mb-8">
                  Mainframe Solutions is an operational advisory and delivery partner that closes the loop between how organizations hire, how they operate, and how they govern technology. Executive search, AI governance, and digital infrastructure — delivered as one accountable system.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <a href="https://mainframesolutions.net/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-charcoal text-sm font-semibold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap">
                    Explore Mainframe Solutions<i className="ri-arrow-right-line text-sm"></i>
                  </a>
                  <a href="https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-gold text-white text-sm font-semibold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap">
                    Book a Strategy Call<i className="ri-calendar-line text-sm"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TAGLINE STRIP */}
        <section className="py-8 sm:py-10 md:py-14 border-y border-white/5">
          <div ref={taglineReveal.ref} className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto text-center">
            <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 md:gap-10 transition-all duration-700 ${taglineReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              {['Build Leaders', 'Build Software', 'Build the Future'].map((text, i) => (
                <span key={text} className="flex items-center gap-3 sm:gap-6 md:gap-10">
                  <span className="text-white text-base sm:text-lg md:text-xl font-semibold tracking-tight">{text}</span>
                  {i < 2 && <span className="hidden sm:block w-1.5 h-1.5 rounded-full bg-gold/60"></span>}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* OPERATIONAL PERFORMANCE */}
        <section className="py-12 md:py-20 lg:py-24">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div ref={decisionReveal.ref} className="max-w-3xl">
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">The Mainframe Decision Framework</p>
              <h2 className={`text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight mb-4 sm:mb-6 transition-all duration-700 ${decisionReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                Operational Performance Depends on the Decisions Behind It
              </h2>
              <p className={`text-white/50 text-sm md:text-base leading-relaxed transition-all duration-700 delay-150 ${decisionReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                Leadership decisions, hiring outcomes, technology systems, and governance frameworks do not operate independently. When any one of these is misaligned, the impact registers across all of them. Mainframe Solutions works with organizations to align all four as one connected system.
              </p>
            </div>
          </div>
        </section>

        {/* DECISION FRAMEWORK — 3 STEPS */}
        <section className="pb-12 md:pb-20 lg:pb-24">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div ref={frameworkReveal.ref} className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {decisionSteps.map((step, i) => (
                <div key={step.title} className={`bg-dark-card rounded-2xl p-6 sm:p-8 md:p-10 border border-white/5 transition-all duration-700 ${frameworkReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${200 + i * 150}ms` }}>
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gold/10 rounded-lg mb-4 sm:mb-6">
                    <span className="text-gold text-sm font-bold">{i + 1}</span>
                  </div>
                  <h3 className="text-white font-semibold text-base sm:text-lg mb-2 sm:mb-3">{step.title}</h3>
                  <p className="text-white/45 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div ref={servicesHeaderReveal.ref} className={`mb-8 sm:mb-12 transition-all duration-700 ${servicesHeaderReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">Operational &amp; Hiring Systems</p>
              <h2 className="text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight">Services We Deliver</h2>
            </div>
            <div ref={servicesListReveal.ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {services.map((service, index) => (
                <div key={service.id} className={`bg-dark-card rounded-xl p-5 sm:p-6 md:p-8 border border-white/5 hover:border-gold/20 transition-all duration-500 ${servicesListReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/5 rounded-lg shrink-0"><i className={`${service.icon} text-gold text-lg sm:text-xl`}></i></div>
                    <div><h3 className="text-white font-semibold text-base sm:text-lg mb-2">{service.title}</h3><p className="text-white/45 text-sm leading-relaxed">{service.description}</p></div>
                  </div>
                </div>
              ))}
              {[
                { icon: 'ri-global-line', title: 'Company Website & Digital Platform', desc: 'Professional websites and digital platforms built with SEO architecture, CRM integration, and lead workflow automation to support ongoing business development.' },
                { icon: 'ri-shield-check-line', title: 'AI Governance & Operational Intelligence', desc: 'Advisory frameworks governing responsible AI use across hiring, operational systems, procurement workflows, and technology deployments.' },
                { icon: 'ri-plug-line', title: 'API Integration & Workflow Connectivity', desc: 'Integration of CRM, HRIS, project management, and operational platforms into unified environments that reduce manual effort and increase data visibility.' },
                { icon: 'ri-brain-line', title: 'BuildFit AI Leadership Evaluation', desc: 'Structured candidate evaluation producing competency-based assessments and documented hiring decisions for every leadership search.' },
              ].map((item, i) => (
                <div key={item.title} className={`bg-dark-card rounded-xl p-5 sm:p-6 md:p-8 border border-white/5 hover:border-gold/20 transition-all duration-500 ${servicesListReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${(services.length + i) * 100}ms` }}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/5 rounded-lg shrink-0"><i className={`${item.icon} text-gold text-lg sm:text-xl`}></i></div>
                    <div><h3 className="text-white font-semibold text-base sm:text-lg mb-2">{item.title}</h3><p className="text-white/45 text-sm leading-relaxed">{item.desc}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FULL LOOP */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div ref={loopReveal.ref} className="mb-8 sm:mb-12">
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">One Partner. One System. The Full Loop.</p>
              <h2 className={`text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight mb-4 transition-all duration-700 ${loopReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                The systems behind clear leadership decisions and scalable workforce operations.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {loopSteps.map((step, i) => (
                <div key={step.label} className={`bg-dark-card rounded-xl p-5 sm:p-6 md:p-8 border border-white/5 transition-all duration-700 ${loopReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${200 + i * 100}ms` }}>
                  <div className="flex items-center gap-3 mb-3 sm:mb-4 flex-wrap">
                    <span className="text-gold text-xs font-bold bg-gold/10 px-3 py-1 rounded-full">Step {step.num}</span>
                    <span className="text-white/30 text-xs font-medium">{step.sub}</span>
                  </div>
                  <h3 className="text-white font-semibold text-sm sm:text-base mb-2">{step.label} {step.sub !== step.label && `(${step.sub})`}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className={`mt-8 sm:mt-10 bg-dark-surface/50 rounded-xl p-5 sm:p-6 md:p-8 border border-white/5 transition-all duration-700 delay-700 ${loopReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <p className="text-white/50 text-sm md:text-base leading-relaxed">Organizations that engage across multiple steps benefit from shared context. Assessment findings inform governance design. Governance informs system requirements. System requirements shape the talent profile. Each engagement builds on the previous.</p>
            </div>
          </div>
        </section>

        {/* WHY */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div ref={whyReveal.ref} className="mb-8 sm:mb-10">
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">Why companies choose Mainframe Solutions</p>
              <h2 className={`text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight transition-all duration-700 ${whyReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>Built Different. Proprietary. Accountable.</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {whyItems.map((item, i) => (
                <div key={item.title} className={`flex gap-3 sm:gap-4 bg-dark-card rounded-xl p-5 sm:p-6 md:p-8 border border-white/5 transition-all duration-700 ${whyReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${200 + i * 100}ms` }}>
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gold/10 rounded-lg shrink-0"><i className="ri-check-line text-gold"></i></div>
                  <div><h3 className="text-white font-semibold text-sm sm:text-base mb-2">{item.title}</h3><p className="text-white/40 text-sm leading-relaxed">{item.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Relationships & Ecosystem */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div ref={ecosystemReveal.ref} className="mb-8 sm:mb-10">
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">Featured Relationships & Ecosystem</p>
              <h2 className={`text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight transition-all duration-700 ${ecosystemReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                Industry Affiliations & Memberships
              </h2>
              <p className={`text-white/50 text-sm md:text-base leading-relaxed max-w-xl mt-4 transition-all duration-700 delay-100 ${ecosystemReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                Mainframe Solutions maintains active involvement in recognized industry, HR, and federal organizations. These affiliations reflect our commitment to operational standards and reliable execution in complex environments.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-5">
              {ecosystemPartners.map((partner, i) => (
                <div
                  key={partner.name}
                  className={`bg-dark-card rounded-xl p-5 sm:p-6 border border-white/5 hover:border-gold/20 transition-all duration-500 flex flex-col items-center text-center ${ecosystemReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${200 + i * 80}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-4">
                    <i className={`${partner.icon} text-gold text-xl`}></i>
                  </div>
                  <p className="text-white font-semibold text-sm leading-tight">{partner.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLIENT CASE STUDIES */}
        <CaseStudiesSection />

        {/* PACKAGE SECTION */}
        <section ref={packageReveal.ref} className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 sm:gap-6 mb-10 sm:mb-16 transition-all duration-700 ${packageReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div>
                <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">Infrastructure Growth Package</p>
                <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold leading-tight">Growth Infrastructure</h2>
              </div>
              <p className="text-white/50 text-sm md:text-base max-w-xl leading-relaxed">A connected implementation package designed to help organizations improve hiring, modernize websites, reduce repetitive workflows, implement AI systems, and strengthen operational infrastructure.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[
                { delay: '200ms', label: 'Starting At', content: (
                  <>
                    <p className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-8 sm:mb-10">{packageData.startingPrice}</p>
                    <div className="mt-auto rounded-xl overflow-hidden bg-dark-surface border border-white/5 p-5 sm:p-6 md:p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gold/10 rounded-lg"><i className="ri-stack-line text-gold"></i></div>
                        <span className="text-white font-semibold text-sm">Infrastructure Stack</span>
                      </div>
                      <p className="text-white/40 text-sm leading-relaxed">Connected hiring, governance, software, and digital infrastructure delivered as one accountable engagement.</p>
                    </div>
                  </>
                )},
                { delay: '350ms', label: 'Included', content: (
                  <ul className="space-y-4 sm:space-y-5">
                    {packageData.included.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full border border-white/15 mt-0.5 shrink-0"><i className="ri-check-line text-gold text-xs"></i></span>
                        <span className="text-white/80 text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                )},
                { delay: '500ms', label: 'Outcomes', content: (
                  <>
                    <ul className="space-y-4 sm:space-y-5 mb-6 sm:mb-8">
                      {packageData.outcomes.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center rounded-full border border-white/15 mt-0.5 shrink-0"><i className="ri-check-line text-gold text-xs"></i></span>
                          <span className="text-white/80 text-sm leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-6 border-t border-white/10"><p className="text-white/40 text-sm leading-relaxed">{packageData.footerNote}</p></div>
                  </>
                )},
              ].map(({ delay, label, content }) => (
                <div key={label} className={`bg-dark-card rounded-2xl p-6 sm:p-8 md:p-10 flex flex-col transition-all duration-700 ${packageReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: delay }}>
                  <p className="text-gold text-xs font-semibold tracking-[0.2em] uppercase mb-4 sm:mb-6">{label}</p>
                  {content}
                </div>
              ))}
            </div>
            <div className={`mt-8 sm:mt-12 flex justify-center transition-all duration-700 delay-600 ${packageReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <a href={packageData.ctaLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-charcoal text-sm font-semibold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap">
                {packageData.ctaText}<i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className="py-12 md:py-16 lg:py-20 border-t border-white/5">
          <div ref={ctaReveal.ref} className={`w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-3xl mx-auto text-center transition-all duration-700 ${ctaReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-white text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Ready to modernize your operational backbone?</h2>
            <p className="text-white/50 text-sm md:text-base mb-6 sm:mb-8">Let's discuss how Mainframe Solutions brings hiring, systems, governance, and digital infrastructure together in one engagement.</p>
            <a href="https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-charcoal text-sm font-semibold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap">
              Start the Conversation<i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}